import qs from 'qs';
import moment from '@narvar/moment-shim';
import { PICKUP_ADDON, SUBMIT_PICKUP } from '../../constants/endpoints';
import { getUrlInfo } from '../../lib/url';
import { configureStripeWidget } from '../stripe';
import { PICKUPS } from '../../constants/stripeWidgets';
import {
  NUMBER_OF_PICKUP_DAYS,
  DATE_PICKER_START_OFFSET_IN_DAYS,
  API_DATE_FORMAT,
  ALL_DAY_TIME_WINDOW,
} from '../../constants/pickups';

export const RECEIVE_PICKUP_ADDON_DATA = 'RECEIVE_PICKUP_ADDON_DATA';
export const RECEIVE_PICKUP_ADDON_ERROR = 'RECEIVE_PICKUP_ADDON_ERROR';
export const RECEIVE_PICKUP_SUBMITTING = 'RECEIVE_PICKUP_ADDON_SUBMITTING';
export const RECEIVE_PICKUP_SUBMIT_SUCCESS = 'RECEIVE_PICKUP_ADDON_SUBMIT_SUCCESS';
export const TOGGLE_PICKUP_INSTRUCTIONS = 'TOGGLE_PICKUP_ADDON_INSTRUCTIONS';
export const UPDATE_PICKUP_INSTRUCTIONS = 'UPDATE_PICKUP_ADDON_INSTRUCTIONS';
export const RECEIVE_IS_FETCHING_PICKUP = 'RECEIVE_IS_FETCHING_PICKUP_ADDON';
export const RECEIVE_PICKUP_TYPE_SELECTION = 'RECEIVE_PICKUP_ADDON_TYPE_SELECTION';
export const RECEIVE_PICKUP_DATE_SELECTION = 'RECEIVE_PICKUP_ADDON_DATE_SELECTION';
export const RECEIVE_PICKUP_PHONE_NUMBER = 'RECEIVE_PICKUP_ADDON_PHONE_NUMBER';
export const RECEIVE_PICKUP_PHONE_NUMBER_ERROR = 'RECEIVE_PICKUP_ADDON_PHONE_NUMBER_ERROR';
export const RECEIVE_PICKUP_TIME_WINDOW = 'RECEIVE_PICKUP_TIME_WINDOW';

const calculateFee = (fee) => {
  // These first checks can be removed when we deprecate service_fee and only use currency from the fee object.
  if (!fee) {
    return {
      totalFee: null,
      originalFee: null,
      feeReason: null,
      currency: null,
    };
  }
  return {
    totalFee: fee.total_fee || null,
    originalFee: fee.original_fee || null,
    feeReason: fee.reason || null,
    currency: fee.currency || null,
  };
};

export const fetchPickupAddon = () => async (dispatch, getState) => {
  try {
    const {
      params: { return_id: returnId },
    } = getUrlInfo();
    const { retailerSettings: { name: retailerName } = {} } = getState();

    dispatch({
      type: RECEIVE_IS_FETCHING_PICKUP,
      data: {
        isFetching: true,
      },
    });

    const {
      fee,
      program,
      client_secret: paymentIntentSecret,
      stripe_publishable_key: publishableKey,
      narvar_payment_id: narvarPaymentId,
      pickup_address: address,
      pickup_slots: pickupSlots,
      pickup_slot: pickupSlot,
      pickup_types: pickupTypes,
    } = await fetch(
      PICKUP_ADDON({
        params: qs.stringify({ return_id: returnId, number_of_days: NUMBER_OF_PICKUP_DAYS }),
        retailerName,
      }),
    ).then((e) => e.json());

    if (!address || !pickupSlots || !pickupSlots.length) {
      return dispatch({
        type: RECEIVE_PICKUP_ADDON_ERROR,
      });
    }

    const { feeReason, totalFee, originalFee, currency } = calculateFee(fee);
    const paymentRequired = totalFee > 0;

    await dispatch(
      configureStripeWidget({
        id: PICKUPS,
        serviceFee: totalFee,
        paymentIntentSecret,
        publishableKey,
        completedPayment: false,
        errorMessage: '',
      }),
    );

    const allDates = [...Array(NUMBER_OF_PICKUP_DAYS)]
      .map((e, i) =>
        moment()
          .add(DATE_PICKER_START_OFFSET_IN_DAYS + i, 'days')
          .format(API_DATE_FORMAT),
      )
      .map((date) => ({
        date,
        eligible: pickupSlots.find((slot) => slot.date === date) || false,
      }));

    return dispatch({
      type: RECEIVE_PICKUP_ADDON_DATA,
      data: {
        address,
        options: {
          date: allDates,
          pickupTypes,
          timeWindows: pickupSlot.open_all_day ? [] : pickupSlot.time_window,
        },
        form: {
          program,
          narvarPaymentId,
          date: pickupSlots[0].date,
          serviceFee: totalFee,
          fee: {
            feeReason,
            totalFee,
            originalFee,
            currency,
          },
          paymentReq: paymentRequired,
          pickupType: null,
          instructions: '',
        },
      },
    });
  } catch (err) {
    dispatch({
      type: RECEIVE_PICKUP_ADDON_ERROR,
      data: {
        error: true,
      },
    });
    return console.error(err);
  }
};

export const submitPickup = () => async (dispatch, getState) => {
  try {
    const {
      page: {
        browserFingerprint,
        initialPageLoadParams: { return_id: encryptedReturnId },
      },
      pickupAddon: {
        form: { narvarPaymentId, date, pickupTime, pickupType, instructions, program, phoneNumber },
      },
      retailerSettings: { name: retailerName } = {},
    } = getState();

    const response = await fetch(SUBMIT_PICKUP({ retailerName }), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'narvar-tfid': browserFingerprint,
      },
      body: JSON.stringify({
        encrypted_return_id: encryptedReturnId,
        narvar_payment_id: narvarPaymentId,
        pickup_date: date,
        pickup_time: pickupTime || ALL_DAY_TIME_WINDOW,
        pickup_type: pickupType,
        pickup_instructions: instructions,
        program,
        ...(phoneNumber
          ? {
              pickup_contact: {
                phone_number: phoneNumber,
              },
            }
          : {}),
      }),
    });

    const {
      schedule_data: { email, pickup_date: pickupDate },
    } = await response.json();

    return dispatch({
      type: RECEIVE_PICKUP_SUBMIT_SUCCESS,
      data: {
        email,
        pickupDate,
      },
    });
  } catch (err) {
    dispatch({
      type: RECEIVE_PICKUP_ADDON_ERROR,
      data: {
        error: true,
      },
    });
    return console.error(err);
  }
};

export const updatePickupInstructions = (instructions) => ({
  type: UPDATE_PICKUP_INSTRUCTIONS,
  data: {
    instructions,
  },
});

export const receivePickupSubmitting = (submitting) => ({
  type: RECEIVE_PICKUP_SUBMITTING,
  data: {
    submitting,
  },
});

export const receivePickupTypeSelection = (pickupType) => ({
  type: RECEIVE_PICKUP_TYPE_SELECTION,
  data: {
    pickupType,
  },
});

export const receivePickupDateSelection = (date) => ({
  type: RECEIVE_PICKUP_DATE_SELECTION,
  data: {
    date,
  },
});

export const receivePickupPhoneNumber = (phoneNumber) => ({
  type: RECEIVE_PICKUP_PHONE_NUMBER,
  data: {
    phoneNumber,
  },
});

export const receivePickupPhoneError = (phoneNumberError) => ({
  type: RECEIVE_PICKUP_PHONE_NUMBER_ERROR,
  data: {
    phoneNumberError,
  },
});

export const receivePickupTimeWindow = (pickupTime) => ({
  type: RECEIVE_PICKUP_TIME_WINDOW,
  data: {
    pickupTime,
  },
});
